import React, { useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, FileText, CheckCircle } from 'lucide-react';
import { accessoryCategories } from '../../data/accessories';
import RequestTesterModal from './RequestTesterModal';

const AccessoryModal = ({ isOpen, onClose, accessory, onRequestSample, onRequestQuote }) => {
  const [isTesterModalOpen, setIsTesterModalOpen] = useState(false);
  // Handle ESC key press
  const handleEscKey = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscKey]);

  // Handle click outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!accessory) return null;

  const categoryConfig = accessoryCategories[accessory.category];

  // Render category-specific primary content
  const renderPrimaryContent = () => {
    switch (accessory.category) {
      case 'adhesives':
        return <AdhesiveContent accessory={accessory} />;
      case 'frames':
        return <FrameContent accessory={accessory} />;
      case 'ancillaries':
        return <AncillaryContent accessory={accessory} />;
      default:
        return null;
    }
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
          data-testid="accessory-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
            data-testid="accessory-modal"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              data-testid="accessory-modal-close"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Header */}
            <div className="p-6 pb-4 border-b border-gray-100">
              <span className="inline-block px-3 py-1 mb-2 text-xs font-semibold text-primary bg-primary/10 rounded-full">
                {categoryConfig.label}
              </span>
              <h2 className="text-2xl font-heading font-bold text-gray-900 pr-10">
                {accessory.name}
              </h2>
              <p className="mt-2 text-gray-600">{accessory.descriptor}</p>
            </div>

            {/* Product Visual */}
            <div className="p-6 bg-gray-50">
              <div className="flex items-center justify-center">
                <img
                  src={accessory.image}
                  alt={accessory.name}
                  className="max-h-48 object-contain"
                />
              </div>
              {/* Show SVG profile for frames/ancillaries if available */}
              {accessory.svgProfile && (
                <div className="mt-4 flex items-center justify-center">
                  <img
                    src={accessory.svgProfile}
                    alt={`${accessory.name} profile`}
                    className="max-h-16 object-contain opacity-70"
                  />
                </div>
              )}
            </div>

            {/* Primary Content - Category Specific */}
            <div className="p-6">
              {renderPrimaryContent()}
            </div>

            {/* Works With Section */}
            {accessory.worksWith && accessory.worksWith.length > 0 && (
              <div className="px-6 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Works With</h4>
                <div className="flex flex-wrap gap-2">
                  {accessory.worksWith.map((item) => (
                    <span
                      key={item}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Why Use This Section */}
            {accessory.whyUseThis && accessory.whyUseThis.length > 0 && (
              <div className="px-6 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Why Use This</h4>
                <ul className="space-y-1">
                  {accessory.whyUseThis.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA Section */}
            <div className="p-6 pt-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    if (accessory.category === 'adhesives') {
                      setIsTesterModalOpen(true);
                    } else {
                      onClose();
                      if (onRequestQuote) onRequestQuote(accessory.name);
                    }
                  }}
                  className="flex-1 px-6 py-3 border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition-colors"
                  data-testid="accessory-cta-secondary"
                >
                  {categoryConfig.ctaSecondary}
                </button>
                {accessory.pdfSheet && (
                  <a
                    href={accessory.pdfSheet}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
                    data-testid="accessory-pdf-download"
                  >
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Spec Sheet</span>
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>

    {/* Request Tester Modal (adhesives only) */}
    <RequestTesterModal
      isOpen={isTesterModalOpen}
      onClose={() => setIsTesterModalOpen(false)}
      productName={accessory?.name || ''}
    />
    </>
  );
};

// Adhesive-specific content component
const AdhesiveContent = ({ accessory }) => (
  <div className="space-y-4">
    {/* Best For */}
    {accessory.bestFor && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Best For</h4>
        <ul className="space-y-1">
          {accessory.bestFor.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Performance Highlights */}
    {accessory.performanceHighlights && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Performance Highlights</h4>
        <ul className="space-y-1">
          {accessory.performanceHighlights.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Application Details */}
    {accessory.applicationDetails && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Application</h4>
        <div className="flex flex-wrap gap-2">
          {accessory.applicationDetails.map((item) => (
            <span
              key={item}
              className="px-3 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Frame-specific content component
const FrameContent = ({ accessory }) => (
  <div className="space-y-4">
    {/* Details */}
    {accessory.details && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Specifications</h4>
        <div className="grid grid-cols-2 gap-2">
          {accessory.details.dimensions && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Dimensions</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.dimensions}</span>
            </div>
          )}
          {accessory.details.material && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Material</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.material}</span>
            </div>
          )}
          {accessory.details.drilling && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Drilling</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.drilling}</span>
            </div>
          )}
          {accessory.details.profile && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Profile</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.profile}</span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

// Ancillary-specific content component
const AncillaryContent = ({ accessory }) => (
  <div className="space-y-4">
    {/* Function */}
    {accessory.function && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Function</h4>
        <ul className="space-y-1">
          {accessory.function.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    )}

    {/* Where Used */}
    {accessory.whereUsed && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Where It's Used</h4>
        <div className="flex flex-wrap gap-2">
          {accessory.whereUsed.map((item) => (
            <span
              key={item}
              className="px-3 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    )}

    {/* Details */}
    {accessory.details && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Details</h4>
        <div className="grid grid-cols-2 gap-2">
          {accessory.details.dimensions && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Dimensions</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.dimensions}</span>
            </div>
          )}
          {accessory.details.material && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <span className="text-xs text-gray-500 block">Material</span>
              <span className="text-sm font-medium text-gray-800">{accessory.details.material}</span>
            </div>
          )}
        </div>
      </div>
    )}

    {/* Diagram if available */}
    {accessory.diagramImage && (
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Installation Diagram</h4>
        <div className="p-4 bg-gray-50 rounded-lg">
          <img
            src={accessory.diagramImage}
            alt={`${accessory.name} installation diagram`}
            className="max-h-32 mx-auto object-contain"
          />
        </div>
      </div>
    )}
  </div>
);

export default AccessoryModal;
